import React, { useEffect, useState } from "react";
import {
    Button, Snackbar, Checkbox, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Alert, Typography, Box, TextField,
    Pagination, Chip, IconButton, Collapse
} from "@mui/material";
import { Download, Search, Clear, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { saveAs } from "file-saver";
import BillingReportsService from "../Services/Reports/BillingReportsService";
import Home from "./home/Home";

// Simple date formatter
const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    if (dateStr.includes('-')) {
        const [day, month, year] = dateStr.split('-');
        return `${day}/${month}/${year}`;
    }
    return dateStr;
};

function EInvoice() {
    const [data, setData] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(10);
    const [expandedRows, setExpandedRows] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    // Load data on component mount
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await BillingReportsService.get_einvoice_list();
            const invoiceData = res?.data?.data || [];
            setData(invoiceData);
            console.log("Loaded data:", invoiceData);
        } catch (err) {
            showMessage(err.message || "Failed to load data", "error");
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (message, severity = "info") => {
        setSnackbar({ open: true, message, severity });
    };

    // Group data by invoice number and calculate correct totals
    const getGroupedInvoices = () => {
        const grouped = new Map();

        data.forEach(item => {
            if (!grouped.has(item.invoice_No)) {
                grouped.set(item.invoice_No, {
                    invoice_No: item.invoice_No,
                    invoice_date: item.invoice_date,
                    invoice_date_formatted: formatDate(item.invoice_date),
                    party_name: item.party_name,
                    party_gst_no: item.party_gst_no,
                    party_address: item.party_address,
                    job_no: item.job_no,
                    tot_bill_amt: 0, // Will calculate properly
                    items: []
                });
            }

            // Get the invoice object
            const invoice = grouped.get(item.invoice_No);

            // Add each item/row to the invoice's items array
            invoice.items.push({
                description: item.description,
                hsn_sac_code: item.hsn_sac_code,
                Taxable_amount: parseFloat(item.Taxable_amount) || 0,
                igstsum: parseFloat(item.igstsum) || 0,
                cgstsum: parseFloat(item.cgstsum) || 0,
                sgstsum: parseFloat(item.sgstsum) || 0,
                tot_bill_amt: parseFloat(item.tot_bill_amt) || 0,
                igst: item.igst,
                cgst: item.cgst,
                sgst: item.sgst
            });

            // Update the invoice total by adding this item's total
            invoice.tot_bill_amt += parseFloat(item.tot_bill_amt) || 0;
        });

        return Array.from(grouped.values());
    };

    const invoices = getGroupedInvoices();

    // Filter invoices based on search
    const filteredInvoices = invoices.filter(inv => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            inv.invoice_No.toLowerCase().includes(search) ||
            inv.party_name.toLowerCase().includes(search) ||
            inv.job_no.toLowerCase().includes(search) ||
            inv.items.some(item => item.description.toLowerCase().includes(search))
        );
    });

    // Pagination
    const paginatedInvoices = filteredInvoices.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    // Toggle expand/collapse for items
    const toggleExpand = (invoiceNo) => {
        setExpandedRows(prev => ({
            ...prev,
            [invoiceNo]: !prev[invoiceNo]
        }));
    };

    // Handle checkbox selection (select entire invoice)
    const toggleSelect = (invoiceNo) => {
        setSelected(prev =>
            prev.includes(invoiceNo)
                ? prev.filter(i => i !== invoiceNo)
                : [...prev, invoiceNo]
        );
    };

    const toggleSelectAll = () => {
        if (selected.length === filteredInvoices.length) {
            setSelected([]);
        } else {
            setSelected(filteredInvoices.map(inv => inv.invoice_No));
        }
    };

    // Transform to NIC format (includes all items with correct amounts)
    const transformToNIC = (invoice) => {
        // Calculate totals correctly from items
        const totals = invoice.items.reduce((acc, item) => ({
            assVal: acc.assVal + (item.Taxable_amount || 0),
            igstVal: acc.igstVal + (item.igstsum || 0),
            cgstVal: acc.cgstVal + (item.cgstsum || 0),
            sgstVal: acc.sgstVal + (item.sgstsum || 0),
            totInvVal: acc.totInvVal + (item.tot_bill_amt || 0)
        }), { assVal: 0, igstVal: 0, cgstVal: 0, sgstVal: 0, totInvVal: 0 });

        const formatDateForNIC = (dateStr) => {
            if (!dateStr) return "";
            const [day, month, year] = dateStr.split('-');
            return `${day}/${month}/${year}`;
        };

        const getGstRate = (gstPercent) => {
            return parseFloat(gstPercent?.replace('%', '') || 0);
        };

        // Find first item to get seller details
        const firstDataItem = data.find(d => d.invoice_No === invoice.invoice_No);

        return {
            Version: "1.1",
            TranDtls: {
                TaxSch: "GST",
                SupTyp: "B2B",
                IgstOnIntra: "N",
                RegRev: null,
                EcmGstin: null
            },
            DocDtls: {
                Typ: "INV",
                No: invoice.invoice_No,
                Dt: formatDateForNIC(invoice.invoice_date)
            },
            SellerDtls: {
                Gstin: firstDataItem?.Seller_Gstin || "",
                LglNm: firstDataItem?.LglNm || "N G Pillai Pvt Ltd",
                TrdNm: null,
                Addr1: firstDataItem?.Seller_Addr1 || "",
                Addr2: null,
                Loc: firstDataItem?.Seller_Loc || "",
                Pin: parseInt(firstDataItem?.Seller_Pin) || 0,
                Stcd: firstDataItem?.Seller_Stcd || "",
                Ph: null,
                Em: null
            },
            BuyerDtls: {
                Gstin: invoice.party_gst_no || "",
                LglNm: invoice.party_name || "",
                TrdNm: null,
                Pos: firstDataItem?.party_state_code || "",
                Addr1: invoice.party_address || "",
                Addr2: null,
                Loc: firstDataItem?.party_state || "",
                Pin: Number(firstDataItem?.party_pin_code) || "",
                Stcd: firstDataItem?.party_state_code || "",
                Ph: null,
                Em: null
            },
            ValDtls: {
                AssVal: Math.round(totals.assVal),
                IgstVal: Math.round(totals.igstVal),
                CgstVal: Math.round(totals.cgstVal),
                SgstVal: Math.round(totals.sgstVal),
                CesVal: 0,
                StCesVal: 0,
                Discount: 0,
                OthChrg: 0,
                RndOffAmt: 0,
                TotInvVal: Math.round(totals.totInvVal)
            },
            ItemList: invoice.items.map((item, index) => {
                const taxableAmt = item.Taxable_amount || 0;
                const igstAmt = item.igstsum || 0;
                const cgstAmt = item.cgstsum || 0;
                const sgstAmt = item.sgstsum || 0;

                let gstRate = 0;
                if (igstAmt > 0 && taxableAmt > 0) {
                    gstRate = Math.round((igstAmt / taxableAmt) * 100);
                } else if (cgstAmt > 0 && taxableAmt > 0) {
                    gstRate = Math.round(((cgstAmt + sgstAmt) / taxableAmt) * 100);
                }

                return {
                    SlNo: String(index + 1),
                    PrdDesc: item.description || "",
                    IsServc: "Y",
                    HsnCd: item.hsn_sac_code || "",
                    Qty: 1,
                    Unit: "NOS",
                    UnitPrice: Math.round(taxableAmt),
                    TotAmt: Math.round(taxableAmt),
                    Discount: 0,
                    PreTaxVal: 0,
                    AssAmt: Math.round(taxableAmt),
                    GstRt: gstRate,
                    IgstAmt: Math.round(igstAmt),
                    CgstAmt: Math.round(cgstAmt),
                    SgstAmt: Math.round(sgstAmt),
                    CesRt: 0,
                    CesAmt: 0,
                    CesNonAdvlAmt: 0,
                    StateCesRt: 0,
                    StateCesAmt: 0,
                    StateCesNonAdvlAmt: 0,
                    OthChrg: 0,
                    TotItemVal: Math.round(item.tot_bill_amt || 0)
                };
            })
        };
    };

    // Download JSON
    const downloadJSON = () => {
        if (selected.length === 0) {
            showMessage("Please select at least one invoice", "warning");
            return;
        }

        try {
            const selectedInvoices = invoices.filter(inv => selected.includes(inv.invoice_No));
            const results = selectedInvoices.map(inv => transformToNIC(inv));
            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
            const filename = `EInvoice_${timestamp}.json`;

            const blob = new Blob([JSON.stringify(results, null, 2)], { type: "application/json" });
            saveAs(blob, filename);

            const totalItems = results.reduce((sum, inv) => sum + inv.ItemList.length, 0);
            showMessage(`Downloaded ${selected.length} invoice(s) with ${totalItems} items`, "success");
        } catch (error) {
            showMessage(`Error: ${error.message}`, "error");
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        const num = parseFloat(amount);
        return isNaN(num) ? "0.00" : num.toLocaleString('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    return (
        <>
            <Home />
            <Box sx={{ p: 3, ml: '80px' }}>
                <Typography variant="h4" gutterBottom>
                    E-Invoice Generator
                </Typography>

                {/* Search Bar */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <TextField
                        fullWidth
                        placeholder="Search by Invoice No, Party Name, Job No, or Description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <Search sx={{ mr: 1, color: 'gray' }} />,
                            endAdornment: searchTerm && (
                                <Clear sx={{ cursor: 'pointer' }} onClick={() => setSearchTerm("")} />
                            )
                        }}
                    />
                </Paper>

                {/* Action Buttons */}
                <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        onClick={toggleSelectAll}
                        disabled={filteredInvoices.length === 0}
                    >
                        {selected.length === filteredInvoices.length ? "Deselect All" : "Select All"}
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Download />}
                        onClick={downloadJSON}
                        disabled={selected.length === 0}
                    >
                        Download ({selected.length} invoices)
                    </Button>
                </Box>

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Table */}
                {!loading && (
                    <>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selected.length === filteredInvoices.length && filteredInvoices.length > 0}
                                                indeterminate={selected.length > 0 && selected.length < filteredInvoices.length}
                                                onChange={toggleSelectAll}
                                            />
                                        </TableCell>
                                        <TableCell width={50}></TableCell>
                                        <TableCell><strong>Invoice No</strong></TableCell>
                                        <TableCell><strong>Date</strong></TableCell>
                                        <TableCell><strong>Party Name</strong></TableCell>
                                        <TableCell><strong>GST No</strong></TableCell>
                                        <TableCell><strong>Job No</strong></TableCell>
                                        <TableCell><strong>Items</strong></TableCell>
                                        <TableCell align="right"><strong>Total Amount (₹)</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedInvoices.map((invoice) => (
                                        <React.Fragment key={invoice.invoice_No}>
                                            {/* Main Invoice Row */}
                                            <TableRow
                                                hover
                                                selected={selected.includes(invoice.invoice_No)}
                                                sx={{ '& > *': { borderBottom: 'unset' } }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selected.includes(invoice.invoice_No)}
                                                        onChange={() => toggleSelect(invoice.invoice_No)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleExpand(invoice.invoice_No)}
                                                    >
                                                        {expandedRows[invoice.invoice_No] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={invoice.invoice_No} size="small" color="primary" />
                                                </TableCell>
                                                <TableCell>{invoice.invoice_date_formatted}</TableCell>
                                                <TableCell>{invoice.party_name}</TableCell>
                                                <TableCell>{invoice.party_gst_no || "N/A"}</TableCell>
                                                <TableCell>{invoice.job_no}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={`${invoice.items.length} items`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography fontWeight="bold" color="primary" variant="h6">
                                                        {formatCurrency(invoice.tot_bill_amt)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>

                                            {/* Expanded Items Row */}
                                            <TableRow>
                                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                                                    <Collapse in={expandedRows[invoice.invoice_No]} timeout="auto" unmountOnExit>
                                                        <Box sx={{ margin: 2 }}>
                                                            <Typography variant="h6" gutterBottom component="div">
                                                                Invoice Items - Total: {formatCurrency(invoice.tot_bill_amt)}
                                                            </Typography>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                                                                        <TableCell><strong>#</strong></TableCell>
                                                                        <TableCell><strong>Description</strong></TableCell>
                                                                        <TableCell><strong>HSN/SAC</strong></TableCell>
                                                                        <TableCell align="right"><strong>Taxable Amount (₹)</strong></TableCell>
                                                                        <TableCell align="right"><strong>IGST (₹)</strong></TableCell>
                                                                        <TableCell align="right"><strong>CGST (₹)</strong></TableCell>
                                                                        <TableCell align="right"><strong>SGST (₹)</strong></TableCell>
                                                                        <TableCell align="right"><strong>Total (₹)</strong></TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {invoice.items.map((item, idx) => (
                                                                        <TableRow key={idx}>
                                                                            <TableCell>{idx + 1}</TableCell>
                                                                            <TableCell>{item.description}</TableCell>
                                                                            <TableCell>{item.hsn_sac_code}</TableCell>
                                                                            <TableCell align="right">{formatCurrency(item.Taxable_amount)}</TableCell>
                                                                            <TableCell align="right">{formatCurrency(item.igstsum)}</TableCell>
                                                                            <TableCell align="right">{formatCurrency(item.cgstsum)}</TableCell>
                                                                            <TableCell align="right">{formatCurrency(item.sgstsum)}</TableCell>
                                                                            <TableCell align="right">
                                                                                <Typography fontWeight="bold">
                                                                                    {formatCurrency(item.tot_bill_amt)}
                                                                                </Typography>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                    {/* Summary Row */}
                                                                    <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                                                                        <TableCell colSpan={3} align="right">
                                                                            <strong>Total:</strong>
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            <strong>{formatCurrency(invoice.items.reduce((sum, i) => sum + i.Taxable_amount, 0))}</strong>
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            <strong>{formatCurrency(invoice.items.reduce((sum, i) => sum + i.igstsum, 0))}</strong>
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            <strong>{formatCurrency(invoice.items.reduce((sum, i) => sum + i.cgstsum, 0))}</strong>
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            <strong>{formatCurrency(invoice.items.reduce((sum, i) => sum + i.sgstsum, 0))}</strong>
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            <strong>{formatCurrency(invoice.tot_bill_amt)}</strong>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                </TableBody>
                                                            </Table>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Pagination */}
                        {filteredInvoices.length > rowsPerPage && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                <Pagination
                                    count={Math.ceil(filteredInvoices.length / rowsPerPage)}
                                    page={page}
                                    onChange={(e, value) => setPage(value)}
                                    color="primary"
                                />
                            </Box>
                        )}

                        {/* No Data Message */}
                        {filteredInvoices.length === 0 && (
                            <Paper sx={{ p: 5, textAlign: 'center' }}>
                                <Typography color="textSecondary">
                                    No invoices found
                                </Typography>
                            </Paper>
                        )}
                    </>
                )}

                {/* Snackbar for messages */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                >
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </>
    );
}

export default EInvoice;